import React, { useState } from 'react';
import { FiEye, FiEyeOff, FiCopy, FiDownload } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useToaster } from '../../../contexts/ToasterContext';
import { authApi } from '../../../mocks/auth';
import AuthLayout from '../../../components/Layout/AuthLayout';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog';

// Validation Schema
const validationSchema = Yup.object().shape({
  org_name: Yup.string()
    .min(2, 'Organization name is too short')
    .max(100, 'Organization name is too long')
    .required('Organization name is required'),
  org_shortname: Yup.string()
    .min(2, 'Short name is too short')
    .max(20, 'Short name is too long')
    .matches(
      /^[a-zA-Z0-9-_]+$/,
      'Short name can only contain letters, numbers, hyphens, and underscores'
    )
    .required('Organization short name is required'),
  user_fullname: Yup.string()
    .min(2, 'Name is too short')
    .max(50, 'Name is too long')
    .required('Admin full name is required'),
  user_email: Yup.string().email('Invalid email address').required('Admin email is required'),
  password: Yup.string()
    .min(16, 'Password must be at least 16 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]+$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character (!@#$%^&*)'
    )
    .test(
      'no-consecutive-chars',
      'Password cannot have more than 3 consecutive repeated characters',
      function(value) {
        if (!value) return true;
        const consecutivePattern = /(.)\\1{3,}/;
        return !consecutivePattern.test(value);
      }
    )
    .required('Password is required'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Please confirm your password'),
  public_key: Yup.string()
});

const SignupPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [orgData, setOrgData] = useState(null);
  const [backupCodesDownloaded, setBackupCodesDownloaded] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToaster();

  const copyBackupCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      showToast('Backup code copied to clipboard', 'success');
    } catch (error) {
      showToast('Failed to copy backup code', 'error');
    }
  };

  const downloadBackupCodes = () => {
    const content = backupCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${orgData?.short_name || 'org'}-backup-codes.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setBackupCodesDownloaded(true);
    showToast('Backup codes downloaded successfully', 'success');
  };

  const proceedToLogin = () => {
    if (!backupCodesDownloaded) {
      showToast('Please download your backup codes before proceeding', 'error');
      return;
    }
    setShowBackupCodes(false);
    navigate('/login');
  };

  const initialValues = {
    org_name: 'Stackguard Testing',
    org_shortname: 'ST',
    user_fullname: 'Test Admin',
    user_email: 'test@admin.com',
    password: 'Test@123!Test@123!Test@123!',
    confirm_password: 'Test@123!Test@123!Test@123!',
    public_key: ''
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    // BYPASS AUTH: Navigate to dashboard immediately, no localStorage, no API calls
    showToast('Organization created successfully!', 'success');
    navigate("/dashboard", { replace: true });
    setSubmitting(false);
  };

  return (
    <>
      <AuthLayout>
        <div className="space-y-8">
          {/* Logo and Header */}
          <div className="text-center space-y-4">
            <img src="/logo.svg" alt="StackGuard Logo" className="h-[40px] w-[240px] md:h-[50px] md:w-[300px] mx-auto" />
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Create your organization</h1>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                Discover, Remediate and Govern Non-Human Identities (NHIs) across your infrastructure
              </p>
            </div>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, errors, touched, values }) => (
              <Form className="space-y-6">
                {/* Organization Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Organization Name */}
                  <div className="space-y-2">
                    <Field name="org_name">
                      {({ field }) => (
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter organization name"
                          disabled={isSubmitting}
                          className="w-full h-12 px-4"
                        />
                      )}
                    </Field>
                    <ErrorMessage name="org_name">
                      {(msg) => (
                        <div className="text-red-400 text-xs">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>

                  {/* Organization Short Name */}
                  <div className="space-y-2">
                    <Field name="org_shortname">
                      {({ field }) => (
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter short name (e.g., acme)"
                          disabled={isSubmitting}
                          className="w-full h-12 px-4"
                        />
                      )}
                    </Field>
                    <ErrorMessage name="org_shortname">
                      {(msg) => (
                        <div className="text-red-400 text-xs">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>

                {/* Admin Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* First Name */}
                  <div className="space-y-2">
                    <Field name="user_fullname">
                      {({ field, form }) => {
                        const fullName = field.value || '';
                        const [firstName = '', ...lastNameParts] = fullName.split(' ');
                        const lastName = lastNameParts.join(' ');
                        
                        return (
                          <Input
                            type="text"
                            placeholder="Enter first name"
                            value={firstName}
                            onChange={(e) => {
                              const newFirstName = e.target.value;
                              const newFullName = `${newFirstName} ${lastName}`.trim();
                              form.setFieldValue('user_fullname', newFullName);
                            }}
                            disabled={isSubmitting}
                            className="w-full h-12 px-4"
                          />
                        );
                      }}
                    </Field>
                  </div>
                  
                  {/* Last Name */}
                  <div className="space-y-2">
                    <Field name="user_fullname">
                      {({ field, form }) => {
                        const fullName = field.value || '';
                        const [firstName = '', ...lastNameParts] = fullName.split(' ');
                        const lastName = lastNameParts.join(' ');
                        
                        return (
                          <Input
                            type="text"
                            placeholder="Enter last name"
                            value={lastName}
                            onChange={(e) => {
                              const newLastName = e.target.value;
                              const newFullName = `${firstName} ${newLastName}`.trim();
                              form.setFieldValue('user_fullname', newFullName);
                            }}
                            disabled={isSubmitting}
                            className="w-full h-12 px-4"
                          />
                        );
                      }}
                    </Field>
                    <ErrorMessage name="user_fullname">
                      {(msg) => (
                        <div className="text-red-400 text-xs">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Field name="user_email">
                    {({ field }) => (
                      <Input
                        {...field}
                        type="email"
                        placeholder="Enter email"
                        disabled={isSubmitting}
                        className="w-full h-12 px-4"
                      />
                    )}
                  </Field>
                  <ErrorMessage name="user_email">
                    {(msg) => (
                      <div className="text-red-400 text-xs">{msg}</div>
                    )}
                  </ErrorMessage>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <div className="relative">
                    <Field name="password">
                      {({ field }) => (
                        <Input
                          {...field}
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter password"
                          className="w-full h-12 px-4 pr-12"
                          disabled={isSubmitting}
                        />
                      )}
                    </Field>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <FiEyeOff className="h-5 w-5" />
                      ) : (
                        <FiEye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <ErrorMessage name="password">
                    {(msg) => (
                      <div className="text-red-400 text-xs">{msg}</div>
                    )}
                  </ErrorMessage>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <div className="relative">
                    <Field name="confirm_password">
                      {({ field }) => (
                        <Input
                          {...field}
                          type={showPassword ? "text" : "password"}
                          placeholder="Confirm password"
                          className="w-full h-12 px-4 pr-12"
                          disabled={isSubmitting}
                        />
                      )}
                    </Field>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <FiEyeOff className="h-5 w-5" />
                      ) : (
                        <FiEye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <ErrorMessage name="confirm_password">
                    {(msg) => (
                      <div className="text-red-400 text-xs">{msg}</div>
                    )}
                  </ErrorMessage>
                </div>

                {/* License Key */}
                <div className="space-y-2">
                  <Field name="public_key">
                    {({ field }) => (
                      <Textarea
                        {...field}
                        placeholder="Enter license key"
                        rows={3}
                        className="w-full px-4 py-3 resize-vertical"
                        disabled={isSubmitting}
                      />
                    )}
                  </Field>
                  <ErrorMessage name="public_key">
                    {(msg) => (
                      <div className="text-red-400 text-xs">{msg}</div>
                    )}
                  </ErrorMessage>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className={`w-full h-12 text-white py-3 rounded-md font-semibold transition-colors ${
                    values.org_name && values.org_shortname && values.user_fullname && values.user_email && values.password && values.confirm_password
                      ? 'bg-[#44087D] hover:bg-[#350661]' 
                      : 'bg-[#454545] hover:bg-[#333333]'
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating Organization...' : 'Create Organization'}
                </button>

                {/* Sign In Link */}
                <div className="text-center">
                  <p className="text-gray-600">
                    Already have an organization?{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      className="text-[#44087D] hover:underline font-medium"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </AuthLayout>

      {/* Backup Codes Modal */}
      <Dialog open={showBackupCodes} onOpenChange={() => {}}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center text-[#200A57]">
              Save Your Backup Codes
            </DialogTitle>
            <DialogDescription className="text-center text-[#8D82AA]">
              These codes will allow you to recover your account if you lose access. Store them in a
              safe place - you won't see them again!
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm text-[#200A57]">
                  Organization: {orgData?.name} ({orgData?.short_name})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  {backupCodes.map((code, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-[#F4F0FD] rounded-lg border border-[#C0ACF3]"
                    >
                      <code className="text-sm font-mono text-[#200A57] flex-1">{code}</code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyBackupCode(code)}
                        className="ml-2 text-[#8D82AA] hover:text-[#200A57]"
                      >
                        <FiCopy size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <div className="text-yellow-600 mt-1">⚠️</div>
                <div>
                  <p className="text-sm font-semibold text-yellow-800 mb-1">
                    Important Security Notice
                  </p>
                  <ul className="text-xs text-yellow-700 space-y-1">
                    <li>• These backup codes can only be used once each</li>
                    <li>• Store them securely offline (not in cloud storage)</li>
                    <li>• You must download them before proceeding</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={downloadBackupCodes}
              className="bg-[#44087D] hover:bg-[#350661] text-white flex items-center gap-2"
            >
              <FiDownload size={16} />
              Download Backup Codes
            </Button>
            <Button
              onClick={proceedToLogin}
              disabled={!backupCodesDownloaded}
              variant={backupCodesDownloaded ? 'default' : 'outline'}
              className={
                backupCodesDownloaded
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'opacity-50 cursor-not-allowed'
              }
            >
              {backupCodesDownloaded ? 'Proceed to Login' : 'Download Required'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SignupPage;
